import { useState, type FormEvent } from "react";
import { Button, LinkButton } from "@/ui/Button";
import { BoxInput, ErrorNote } from "@/ui/Field";
import { Screen } from "@/ui/Screen";
import { Display, Tiny } from "@/ui/Text";

interface CodeEntryProps {
  readonly destination: string;
  readonly onSubmit: (code: string) => void;
  readonly onResend: () => void;
  readonly error: string | null;
  readonly busy?: boolean;
}

const CODE_LENGTH = 6;

/** The code, entered where the email was, with the action right under it. */
export function CodeEntry({ destination, onSubmit, onResend, error, busy = false }: CodeEntryProps) {
  const [code, setCode] = useState("");
  const digits = code.replace(/\D/g, "");
  const complete = digits.length === CODE_LENGTH;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (complete) onSubmit(digits);
  }

  return (
    <form onSubmit={submit} className="contents">
      <Screen middle>
        <Display>Check your email.</Display>
        <Tiny className="mt-2">We sent a six-digit code to {destination}.</Tiny>
        <div className="mt-8">
          <BoxInput
            label="Code"
            type="text"
            name="one-time-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={CODE_LENGTH}
            autoFocus
            placeholder="123456"
            className="tracking-[0.3em]"
            value={digits}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        <ErrorNote message={error} />
        <div className="mt-6 flex flex-col gap-2">
          <Button type="submit" label="Continue" disabled={!complete} busy={busy} />
          <div className="text-center">
            <LinkButton label="Send it again" onClick={onResend} disabled={busy} />
          </div>
        </div>
      </Screen>
    </form>
  );
}
